import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import type { ContactGroup } from '../../types';
import { createContactGroup, updateContactGroup } from '../../services/contact-groups';

interface ContactGroupDialogProps {
  open: boolean;
  onClose: () => void;
  group?: ContactGroup | null;
  onSaved: () => void;
}

export default function ContactGroupDialog({ open, onClose, group, onSaved }: ContactGroupDialogProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = !!group;

  useEffect(() => {
    if (!open) return;
    if (group) {
      setName(group.name);
      setDescription(group.description);
    } else {
      setName('');
      setDescription('');
    }
  }, [open, group]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit && group) {
        await updateContactGroup(group.id, {
          name: name.trim(),
          description: description.trim(),
        });
      } else {
        await createContactGroup({
          name: name.trim(),
          description: description.trim() || undefined,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save group:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={mobile}>
      <DialogTitle>{isEdit ? 'Edit Group' : 'Create Group'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? <CircularProgress size={22} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
